import { useSignal, type Signal } from "@preact/signals";
import { useEffect } from "preact/hooks";

export interface KeyNavOptions {
  count: number;
  /** Items per row for grids; 1 = vertical list (left/right then go to onLeft/onRight). */
  columns?: number;
  onActivate: (index: number) => void;
  onLeft?: () => void;
  onRight?: () => void;
  /** Pressing 1–9 jumps to that item and activates it. */
  digits?: boolean;
  enabled?: boolean;
}

/**
 * Keyboard navigation for a list or grid of buttons: arrows move, Enter/Space activates,
 * digits jump. Key presses inside text inputs are ignored. Returns the selected index signal.
 */
export function useKeyNav(opts: KeyNavOptions): Signal<number> {
  const index = useSignal(0);
  const { count, columns = 1, onActivate, onLeft, onRight, digits = true, enabled = true } = opts;

  useEffect(() => {
    if (index.value >= count) index.value = Math.max(0, count - 1);
  }, [count]);

  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      )
        return;
      const i = index.value;
      const move = (n: number) => {
        e.preventDefault();
        index.value = Math.max(0, Math.min(count - 1, n));
      };
      switch (e.key) {
        case "ArrowDown":
          move(i + columns);
          break;
        case "ArrowUp":
          move(i - columns);
          break;
        case "ArrowRight":
          if (columns > 1) move(i + 1);
          else if (onRight) {
            e.preventDefault();
            onRight();
          }
          break;
        case "ArrowLeft":
          if (columns > 1) move(i - 1);
          else if (onLeft) {
            e.preventDefault();
            onLeft();
          }
          break;
        case "Enter":
        case " ":
          if (count > 0) {
            e.preventDefault();
            onActivate(i);
          }
          break;
        default:
          if (digits && /^[1-9]$/.test(e.key) && Number(e.key) <= count) {
            e.preventDefault();
            index.value = Number(e.key) - 1;
            onActivate(Number(e.key) - 1);
          }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [count, columns, onActivate, onLeft, onRight, digits, enabled]);

  return index;
}
